import {
  Component,
  Output,
  EventEmitter,
  SimpleChanges,
  OnInit,
  OnChanges,
  HostListener,
  ViewChild,
  ChangeDetectorRef,
  ComponentFactoryResolver,
  Injector,
  OnDestroy,
} from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { ViewEncapsulation, ElementRef, Input } from '@angular/core';
import { FormGroup, UntypedFormBuilder, Validators, FormArray, AbstractControl, FormControl } from '@angular/forms';

import { BehaviorSubject, of, toArray, Subscription } from 'rxjs';
import { merge, Observable, of as observableOf, pipe } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { finalize } from 'rxjs/operators';

import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatSelectChange } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';

import { AuthQuery } from '@app/auth/auth.query';
import { JournalService, Journal, MailService, Mail } from '@core';
import { environment } from '../../environments/environment';
import { Logger } from '@core';

import { ConfirmDialogComponent } from '../@shared/dialog/confirm-dialog/confirm-dialog.component';
import { KundeService, Kunde, KundeResponse } from '../services/kunde.service';
import { DataChangeClientService } from './changes-client.service';
import { ClientStatusService, ClientStatus } from '../services/client-status.service';

declare var $: any;

export interface EmpFilter {
  name: string;
  options: string[];
  defaultValue: string;
  isOpen?: boolean;
}

export interface filterOption {
  name: string;
  value: string;
  isdefault: boolean;
}

@Component({
  selector: 'app-client',
  templateUrl: './client.component.html',
  styleUrls: ['./client.component.scss'],
})
export class ClientComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  displayedColumns: string[] = [
    'id',
    'suchbegriff',
    'status_id',
    'startdatum',
    'enddatum',
    'actions',
  ];

  dataSource: MatTableDataSource<Kunde>;
  allClients: Kunde[] = []; // Store all clients for filtering
  isLoadingResults = true;
  isRateLimitReached = false;
  resultsLength = 0;

  pageSizes = [10, 25, 50, 100];
  pageSize = 10;
  pageIndex = 0;
  sortBy: string = 'suchbegriff';
  sortOrder: 'asc' | 'desc' = 'asc';
  searchValue: string;

  stati: string[] = [];
  selectedStatusFilter: string = 'Alle';
  //niederlassungen: string[] = ['Wien', 'Graz', 'Salzburg', 'Innsbruck', 'Linz'];
  
  empFilters: EmpFilter[] = [];
  defaultValue = 'All';
  filterDictionary = new Map<string, string>();
  filters$ = new BehaviorSubject<string>('');
  searchTerm$ = new BehaviorSubject<string>('');
  private searchTimeout: any;

  private subscription: Subscription;

  // Data management
  dataSubject = new BehaviorSubject<Kunde[]>([]);
  clients$: Observable<Kunde[]>;
  totalItems: number;

  constructor(
    private kundeService: KundeService,
    private clientStatusService: ClientStatusService,
    private cfr: ComponentFactoryResolver,
    private _snackBar: MatSnackBar,
    private _fb: UntypedFormBuilder,
    private journalService: JournalService,
    public dialog: MatDialog,
    public dataChangeClientService: DataChangeClientService,
    private injector: Injector,
    private router: Router,
    private route: ActivatedRoute,
    private authQuery: AuthQuery
  ) {
    console.log('ClientComponent: Constructor called');
    console.log('ClientComponent: KundeService available:', !!this.kundeService);
    
    // Initialize dataSource with empty array
    this.dataSource = new MatTableDataSource<Kunde>([]);
    console.log('ClientComponent: dataSource initialized');
  }

  ngOnInit() {
    console.log('ClientComponent: ngOnInit called');
    
    // Check authentication status
    console.log('ClientComponent: User authenticated:', this.authQuery.isLoggedIn);
    console.log('ClientComponent: Access token available:', !!this.authQuery.access_token);
    console.log('ClientComponent: Access token value:', this.authQuery.access_token);
    
    // Initialize status options from environment
    this.initializeStatusOptions();
    
    // Initialize filters
    this.empFilters.push({ name: 'Status', options: ['Alle', ...this.stati], defaultValue: 'Alle', isOpen: false });
    //this.empFilters.push({ name: 'Niederlassung', options: ['All', ...this.niederlassungen], defaultValue: this.defaultValue, isOpen: false });

    console.log('ClientComponent: Filters initialized:', this.empFilters);

    // Add click outside listener to close dropdowns
    document.addEventListener('click', this.onDocumentClick.bind(this));

    // Subscribe to the sidebar changes
    this.subscription = this.dataChangeClientService.dataChanges$.subscribe((data) => {
      if (data) {
        console.log('Client change received:', data);
        const mappedData = {
          id: data.id,
          suchbegriff: data.suchbegriff,
          niederlassung_code: data.niederlassung_code,
          status_id: data.status_id,
          techniker_id: data.techniker_id,
          startdatum: data.startdatum,
          enddatum: data.enddatum,
        };
        this.updateClient(mappedData);
      }
    });

    // Data loading will be handled in ngAfterViewInit
    console.log('ClientComponent: Data loading will be handled in ngAfterViewInit');

    console.log('ClientComponent: ngOnInit completed');
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    // Clear search timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    // Remove click outside listener
    document.removeEventListener('click', this.onDocumentClick.bind(this));
  }

  onDocumentClick(event: Event) {
    // Close all dropdowns when clicking outside
    this.empFilters.forEach(filter => {
      filter.isOpen = false;
    });
  }

  toggleDropdown(filter: EmpFilter, event: Event) {
    event.stopPropagation(); // Prevent the document click from firing immediately
    filter.isOpen = !filter.isOpen;
  }

  /**
   * Update the datasource with the given data from the subscription
   */
  updateClient(updatedClient: any) {
    const data = this.dataSubject.getValue();
    const index = data.findIndex((client) => client.id === updatedClient.id);
    if (index > -1) {
      data[index] = updatedClient;
      this.dataSubject.next(data);
      this.dataSource.data = data;
    }
  }

    ngAfterViewInit() {
    console.log('ClientComponent: ngAfterViewInit called');
    
    // Simple data loading without complex merge observables
    this.loadInitialData();
    
    console.log('ClientComponent: ngAfterViewInit completed');
  }

  loadInitialData() {
    console.log('Loading initial client data...');
    this.isLoadingResults = true;
    
    // Get current search term
    const searchTerm = this.searchValue || '';
    console.log('Loading data with search term:', searchTerm);
    
    // Ensure paginator is hooked
    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
    }
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }

    this.loadPage();
  }

  getTableData$(pageNumber: number, pageSize: number, sort: string, order, searchTerm: string, filtersArgs: string) {
    console.log('ClientComponent: getTableData$ called with params:', {
      pageNumber, pageSize, sort, order, searchTerm, filtersArgs
    });
    const result = this.kundeService.getKunden(pageNumber, pageSize, searchTerm, sort, order);
    console.log('ClientComponent: getTableData$ returning observable:', result);
    return result;
  }

  onSearchInput(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchValue = filterValue;
    this.searchTerm$.next(filterValue);
    
    // Clear existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    
    // Set new timeout for debounced search
    this.searchTimeout = setTimeout(() => {
      this.loadDataWithSearch();
    }, 300); // 300ms delay
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchValue = filterValue;
    this.searchTerm$.next(filterValue);
    
    // Trigger new data load with search term
    this.loadDataWithSearch();
  }

  clearSearch() {
    this.searchValue = '';
    this.searchTerm$.next('');
    
    // Trigger new data load without search term
    this.loadDataWithSearch();
  }

  loadDataWithSearch() {
    console.log('Loading data with search term:', this.searchValue);
    this.isLoadingResults = true;
    // Reset to first page on new search
    this.pageIndex = 0;
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.loadPage();
  }

  applyEmpFilter(ob: any, empfilter: EmpFilter) {
    // Handle both MatSelectChange and custom filter objects
    const selectedValue = ob.value || ob;
    
    // Update the filter's default value to show the selected value
    empfilter.defaultValue = selectedValue;
    
    // Store in filter dictionary
    this.filterDictionary.set(empfilter.name, selectedValue);
    
    // Handle status filtering
    if (empfilter.name === 'Status') {
      this.selectedStatusFilter = selectedValue;
      this.applyStatusFilter();
    }
    
    // Trigger database query via filters$ observable
    var jsonString = JSON.stringify(Array.from(this.filterDictionary.entries()));
    this.filters$.next(jsonString);
    
    console.log('Filter applied:', empfilter.name, '=', selectedValue);
  }

  showPageDetail(client_uuid: string) {
    console.log('showPageDetail called with client_uuid:', client_uuid, 'type:', typeof client_uuid);

    // Validate client_uuid
    if (!client_uuid || client_uuid === '0' || client_uuid === 'new') {
      console.log('Invalid client_uuid, navigating to new client form');
      console.log('client_uuid value:', client_uuid);
      console.log('client_uuid type:', typeof client_uuid);
      
      // Debug: Log the current data source to see what's available
      console.log('Current dataSource data:', this.dataSource.data);
      console.log('First client in data:', this.dataSource.data[0]);
      
      this.router.navigate(['/client-edit/new']);
    } else {
      console.log('Navigating to edit client with UUID:', client_uuid);
      this.router.navigate(['/client-edit', client_uuid]);
    }
  }

  onStatusChange(event: MatSelectChange, client: Kunde) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '250px',
      data: {
        title: 'Confirm Status Change',
        message: `Are you sure you want to change the status of ${client.suchbegriff} to ${event.value}?`,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        client.status_id = parseInt(event.value);
        this.kundeService.updateKunde(client.id, client).subscribe(
          (response) => {
            this._snackBar.open('Client status updated successfully', 'Close', {
              duration: 3000,
            });
          },
          (error) => {
            this._snackBar.open('Error updating client status', 'Close', {
              duration: 3000,
            });
          }
        );
      }
    });
  }





  testAuthentication() {
    console.log('=== AUTHENTICATION TEST ===');
    console.log('User authenticated:', this.authQuery.isLoggedIn);
    console.log('Access token available:', !!this.authQuery.access_token);
    console.log('Access token value:', this.authQuery.access_token);
    console.log('User name:', this.authQuery.name);
    console.log('User role:', this.authQuery.role);
    
    // Test API call with current authentication
    console.log('Testing API call...');
    this.kundeService.getKunden(1, 5, '', 'suchbegriff', 'asc').subscribe(
      (response) => {
        console.log('✅ API call successful:', response);
        this._snackBar.open('Authentication successful! API call worked.', 'Close', {
          duration: 3000,
        });
      },
      (error) => {
        console.error('❌ API call failed:', error);
        this._snackBar.open(`Authentication failed: ${error.status} ${error.statusText}`, 'Close', {
          duration: 5000,
        });
      }
    );
  }

  /**
   * Get CSS classes for status badge based on status object
   */
  getStatusClasses(statusId: number): string {
    if (!statusId) {
      return 'bg-slate-50 text-slate-700 ring-slate-600/20 dark:bg-slate-900/30 dark:text-slate-400 dark:ring-slate-500/50';
    }
    switch (statusId) {
      case 1: return 'bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-900/30 dark:text-green-400 dark:ring-green-500/50';
      case 2: return 'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-900/30 dark:text-red-400 dark:ring-red-500/50';
      case 3: return 'bg-yellow-50 text-yellow-700 ring-yellow-600/20 dark:bg-yellow-900/30 dark:text-yellow-400 dark:ring-yellow-500/50';
      case 4: return 'bg-gray-50 text-gray-700 ring-gray-600/20 dark:bg-gray-900/30 dark:text-gray-400 dark:ring-gray-500/50';
      default: return 'bg-slate-50 text-slate-700 ring-slate-600/20 dark:bg-slate-900/30 dark:text-slate-400 dark:ring-slate-500/50';
    }
  }

  /**
   * Initialize status options from environment
   */
  initializeStatusOptions() {
    const statusArray = (environment as any).client_status || [];
    this.stati = statusArray.map((status: any) => status.label);
    console.log('Status options initialized from environment:', this.stati);
  }

  /**
   * Apply status filter to the data source
   */
  applyStatusFilter() {
    if (this.selectedStatusFilter === 'Alle') {
      // Show all clients
      this.dataSource.data = this.allClients || [];
    } else {
      // Filter by selected status
      const statusArray = (environment as any).client_status || [];
      const selectedStatus = statusArray.find((status: any) => status.label === this.selectedStatusFilter);
      
      if (selectedStatus) {
        this.dataSource.data = (this.allClients || []).filter(client => 
          client.status_id === selectedStatus.id
        );
      } else {
        this.dataSource.data = this.allClients || [];
      }
    }
    
    // Update paginator
    if (this.paginator) {
      this.paginator.firstPage();
      this.pageIndex = 0;
    }
    
    console.log('Status filter applied:', this.selectedStatusFilter, 'Filtered data count:', this.dataSource.data.length);
  }

  /**
   * Handle paginator page events
   */
  onPageEvent(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadPage();
  }

  /**
   * Fetch one page from server using current paginator, search, and sort
   */
  private loadPage() {
    const pageNumber = this.pageIndex + 1; // API is 1-based
    const limit = this.pageSize;
    const searchTerm = this.searchValue || '';
    const sortBy = this.sortBy;
    const sortOrder = this.sortOrder;

    this.isLoadingResults = true;
    this.kundeService.getKunden(pageNumber, limit, searchTerm, sortBy, sortOrder).subscribe(
      (response) => {
        this.allClients = response.data || [];
        this.dataSource.data = response.data || [];
        this.totalItems = response.total || 0;
        this.isLoadingResults = false;
        this.isRateLimitReached = false;

        // Apply status filter if selected
        if (this.selectedStatusFilter !== 'Alle') {
          this.applyStatusFilter();
        }
      },
      (error) => {
        console.error('Error loading page:', error);
        this.isLoadingResults = false;
        this.isRateLimitReached = true;
      }
    );
  }

  /**
   * Get status description by status ID using environment array
   */
  getStatusDescription(statusId: number): string {
    const statusArray = (environment as any).client_status || [];
    const status = statusArray.find((s: any) => s.id === statusId);
    return status ? status.label : 'Unbekannt';
  }

  /**
   * Format date for display in German format
   */
  formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE');
  }
}
